import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageHero from '@/components/shared/PageHero';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
    ShieldCheck,
    Package,
    Sparkles,
    FileText,
    Scale,
    ArrowRight,
} from 'lucide-react';
import useSEO from '@/hooks/useSEO';

const SUBSECTIONS = [
    {
        icon: ShieldCheck,
        title: 'Our CSR Eligibility Documents',
        to: '/csr/eligibility-documents',
        blurb: '80G, 12A, CSR-1 and other statutory registrations — downloadable.',
        testId: 'csr-hub-eligibility',
    },
    {
        icon: Package,
        title: 'Our CSR Projects',
        to: '/csr/our-projects',
        blurb: 'Ongoing and completed on-ground initiatives across our seven sectors.',
        testId: 'csr-hub-our-projects',
    },
    {
        icon: Sparkles,
        title: 'Proposal Projects',
        to: '/csr/proposal-projects',
        blurb: 'Ready-to-fund proposals curated for corporate CSR partners.',
        testId: 'csr-hub-proposal-projects',
    },
    {
        icon: FileText,
        title: 'Project Proposals',
        to: '/csr/project-proposals',
        blurb: 'Downloadable project decks grouped by sector (Education, Health, etc.).',
        testId: 'csr-hub-project-proposals',
    },
    {
        icon: Scale,
        title: 'CSR Guidelines (MCA)',
        to: '/csr/guidelines-mca',
        blurb: 'Section 135 framework and best practices from the Ministry of Corporate Affairs.',
        testId: 'csr-hub-guidelines-mca',
    },
];

export default function CSRProjectsPage() {
    useSEO({
        title: 'CSR Projects',
        description:
            'VVVSI Trust CSR hub — Eligibility Documents, Our CSR Projects, Proposal Projects, Project Proposals and MCA CSR Guidelines. Registered under 80G, 12A and CSR-1.',
    });

    return (
        <div data-testid="csr-projects-hub">
            <PageHero
                eyebrow="CSR Projects"
                title="Everything you need to partner with us."
                subtitle="From statutory compliance documents to detailed project proposals and the regulatory framework — all in one place."
            />

            <section className="section-pad">
                <div className="container-page">
                    <div
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
                        data-testid="csr-hub-grid"
                    >
                        {SUBSECTIONS.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <motion.div
                                    key={s.to}
                                    initial={{ opacity: 0, y: 12 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: i * 0.06 }}
                                    viewport={{ once: true, margin: '-40px' }}
                                >
                                    <Link to={s.to} className="block h-full" data-testid={s.testId}>
                                        <Card className="h-full bg-card border-border card-lift">
                                            <CardContent className="p-6 flex flex-col h-full">
                                                <div className="h-12 w-12 rounded-xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] flex items-center justify-center mb-4">
                                                    <Icon className="h-5 w-5" />
                                                </div>
                                                <div className="font-display font-semibold text-lg leading-snug mb-1">
                                                    {s.title}
                                                </div>
                                                <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                                                    {s.blurb}
                                                </p>
                                                <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[hsl(var(--primary))]">
                                                    Explore <ArrowRight className="h-3.5 w-3.5" />
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Partner CTA */}
            <section className="section-pad">
                <div className="container-page">
                    <Card className="bg-[hsl(var(--primary))] text-primary-foreground border-none">
                        <CardContent className="p-8 sm:p-12 grid md:grid-cols-2 gap-6 items-center">
                            <div>
                                <div className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--accent))] mb-2">
                                    Ready to partner?
                                </div>
                                <h3 className="font-display text-2xl sm:text-3xl font-semibold leading-tight">
                                    Let&apos;s design a CSR program that delivers real change.
                                </h3>
                                <p className="mt-3 text-white/85 text-sm leading-relaxed max-w-lg">
                                    Talk to our CSR team about outcome frameworks, budget alignment and quarterly reporting.
                                </p>
                            </div>
                            <div className="flex flex-col sm:flex-row md:justify-end gap-3">
                                <Link to="/csr/project-proposals">
                                    <Button className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90 font-semibold">
                                        View Proposals <ArrowRight className="h-4 w-4 ml-1.5" />
                                    </Button>
                                </Link>
                                <Link to="/contact">
                                    <Button
                                        variant="outline"
                                        className="bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white"
                                        data-testid="csr-contact-us"
                                    >
                                        Talk to CSR Team <ArrowRight className="h-4 w-4 ml-1.5" />
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </section>
        </div>
    );
}
